import { extend, filter, map, pickBy } from 'lodash';
/**
   * @ngdoc service
   * @name adjust.common.service:TimeSpan
   *
   * @description
   * Service used with timeSpan directive within the statistics pages
   *
   */
function TimeSpan(gettext, $location, $timeout) {
  'ngInject';
  /**
   * @ngdoc property
   * @name _timeSpanLabels
   * @propertyOf adjust.common.service:TimeSpan
   *
   * @description
   * Available time span labels
   *
   * @type {Object}
   * @private
   */
  var _timeSpanLabels = {
    overview: [{
      label: gettext('time-span.today'),
      value: 'today',
    }, {
      label: gettext('time-span.yesterday'),
      value: 'yesterday'
    }, {
      label: gettext('time-span.this-week'),
      value: 'this-week'
    }, {
      label: gettext('time-span.last-week'),
      value: 'last-week'
    }, {
      label: gettext('time-span.this-month'),
      value: 'this-month'
    }, {
      label: gettext('time-span.last-month'),
      value: 'last-month'
    }],
    cohorts: [{
      label: gettext('time-span.last-thirty-days'),
      value: 'last-30-days'
    }, {
      label: gettext('time-span.last-sixty-days'),
      value: 'last-60-days'
    }, {
      label: gettext('time-span.last-ninety-days'),
      value: 'last-90-days'
    }, {
      label: gettext('time-span.last-hundred-twenty-days'),
      value: 'last-120-days'
    }],
    secrets: [{
      label: gettext('time-span.this-week'),
      value: 'this-week'
    }, {
      label: gettext('time-span.last-week'),
      value: 'last-week'
    }, {
      label: gettext('time-span.this-month'),
      value: 'this-month'
    }, {
      label: gettext('time-span.last-month'),
      value: 'last-month'
    }]
  };

  /**
   * @ngdoc property
   * @name _screen
   * @propertyOf adjust.common.service:TimeSpan
   *
   * @description
   * Current screen where time span directive has been loaded
   *
   * @type {string}
   * @private
   */
  var _screen;

  /**
   * @ngdoc property
   * @name _startDate
   * @propertyOf adjust.common.service:TimeSpan
   *
   * @description
   * Start date of the app
   *
   * @type {string}
   * @private
   */
  var _startDate;

  /**
   * @ngdoc property
   * @name _allowedParams
   * @propertyOf adjust.common.service:TimeSpan
   *
   * @description
   * List of parameters allowed to change
   *
   * @type {Array}
   * @private
   */
  var _allowedParams = ['from', 'to', 'range'];

  /**
   * @ngdoc property
   * @name _dateFormat
   * @propertyOf adjust.common.service:TimeSpan
   *
   * @description
   * Date format used in **moment()**
   *
   * @type {string}
   * @private
   */
  var _dateFormat = 'YYYY-MM-DD';

  /**
   * @ngdoc property
   * @name _gparams
   * @propertyOf adjust.common.service:TimeSpan
   *
   * @description
   * Parameters object
   *
   * @type {Object}
   * @private
   */
  var _params = {};

  /**
   * @ngdoc property
   * @name timezone
   * @propertyOf adjust.common.service:TimeSpan
   *
   * @description
   * Timezone
   *
   * @type {String}
   * @private
   */
  var _timezone;

  return {
    init: init,
    getTimeSpans: getTimeSpans,
    getParams: getParams,
    adjustParams: adjustParams,
    preserve: preserve,
    getStartDate: getStartDate,
    getToday: getToday,
  };

  /**
   * Set current and previous screen for the time span
   *
   * @param {string} screen current screen where time span is loaded
   * @param {date} startDate starting date of the timespan
   * @param {timezone} timezone moment timezone
   * @returns {Promise}
   */
  function init(screen, startDate, timezone) {

    _screen = screen;
    _startDate = startDate;
    _timezone = timezone;

    return _prepareParams();

  }

  /**
   * Prepare default parameters and extend them with query ones if exist
   *
   * @returns {Promise}
   * @private
   */
  function _prepareParams() {

    const queryParams = _decodeParams();

    return $timeout(() => {
      extend(_params, _getTimeRange(queryParams));
    });

  }

  /**
   * @ngdoc method
   * @name getTimeSpans
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Builds time spans array and filter them by start date
   *
   * @returns {Array} array of time spans
   */
  function getTimeSpans() {

    var spans = _buildTimeSpans();

    var filtered = filter(spans, function(span) {
      return !moment.tz(_startDate, _timezone).isAfter(moment.tz(span.to, _timezone));
    });

    filtered.push({
      label: gettext('time-span.all-time'),
      range: 'all-time',
      from: moment.tz(_startDate, _timezone).format(_dateFormat),
      to: _getToday().format(_dateFormat)
    });
    filtered.push({
      label: gettext('label.custom'),
      range: 'custom'
    });

    return filtered;

  }

  /**
   * @ngdoc method
   * @name getParams
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Get currently set parameters
   *
   * @returns {Object} current parameters
   */
  function getParams() {

    return angular.copy(_params);

  }

  /**
   * @ngdoc method
   * @name adjustParams
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Adjust parameters so their keys match kpi service parameters schema
   *
   * @returns {Object} adjusted parameters
   */
  function adjustParams() {

    var params = getParams();

    return {
      start_date: params.from,
      end_date: params.to
    };

  }

  /**
   * @ngdoc method
   * @name _getToday
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Get today's date but from the start of the day
   *
   *
   * @returns {Object} **moment**'s object which represent today's date
   * @private
   */
  function _getToday() {

    return moment.tz(_timezone).startOf('day');

  }

  /**
   * @ngdoc method
   * @name _buildTimeSpans
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Build time spans from the list of available time labels
   *
   * @returns {Array} array of time spans which have **range**, **from** and **to** parameters
   * @private
   */
  function _buildTimeSpans() {

    var labels = _timeSpanLabels[_screen] || _timeSpanLabels.overview;

    return map(labels, function(label) {
      var span = _buildTimeRange(label.value);
      span.range = label.value;
      span.label = label.label;
      return span;

    });

  }

  /**
   * @ngdoc method
   * @name _buildTimeRange
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Build explicit time ranges using labels like today, last-week and so on
   *
   * @param {String} label descriptive time range
   * @param {Object=} params params initially passed when service is not ready yet
   * @returns {Object} range of time created using label
   */

  function _buildTimeRange(label, params) {

    params = params || getParams();

    var span = {};

    switch (label) {
    case 'today':
      span.from = _getToday();
      span.to = _getToday();
      break;
    case 'yesterday':
      span.from = _getToday().subtract(1, 'day');
      span.to = _getToday().subtract(1, 'day');
      break;
    case 'this-week':
      span.from = _getToday().startOf('isoWeek');
      span.to = _getToday().endOf('isoWeek').startOf('day');
      break;
    case 'this-month':
      span.from = _getToday().startOf('month');
      span.to = _getToday().endOf('month').startOf('day');
      break;
    case 'last-week':
      span.from = _getToday().subtract(1, 'weeks').startOf('isoWeek');
      span.to = _getToday().subtract(1, 'weeks').endOf('isoWeek');
      break;
    case 'last-month':
      span.from = _getToday().subtract(1, 'month').startOf('month');
      span.to = _getToday().subtract(1, 'month').endOf('month');
      break;
    case 'all-time':
      span.from = moment.tz(_startDate, _timezone);
      span.to = _getToday();
      break;
    case 'custom':
      span.from = moment.tz(params.from, _timezone);
      span.to = moment.tz(params.to, _timezone);
      break;
    case 'last-30-days':
    case 'last-60-days':
    case 'last-90-days':
    case 'last-120-days':
      span.from = _getToday().subtract(parseInt(label.split('-')[1]), 'day');
      span.to = _getToday();
      break;
    default:
      if (_screen === 'cohorts') {
        label = 'last-30-days';
        span.from = _getToday().subtract(30, 'day');
        span.to = _getToday();
      } else {
        label = 'this-month';
        span.from = _getToday().startOf('month');
        span.to = _getToday().endOf('month').startOf('day');
      }
      break;
    }

    span.from = span.from.format(_dateFormat);
    span.to = span.to.format(_dateFormat);
    span.range = label;

    if (span.from < _startDate) {
      span.from = _startDate;
    }

    return span;

  }

  /**
   * @ngdoc method
   * @name _decodeParams
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Get parameters allowed to be changed but this service
   *
   * @returns {Object} params object
   * @private
   */
  function _decodeParams() {

    return pickBy($location.search(), function(value, key) {
      return _allowedParams.includes(key);
    });

  }

  /**
   * @ngdoc method
   * @name _getTimeRange
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Prepare time range for the screen depending on previous state and current values
   * - set default time range if range is not defined already
   * - or set default time range if screen has been changed
   *
   *
   * @param {Object} params current params
   * @param {Object} params.from current start date
   * @param {Object} params.to current end date
   * @param {String} params.range time span preset
   * @returns {Object} time range
   * @private
   */
  function _getTimeRange(params) {

    var timeRange = _buildTimeRange(params.range, params);

    preserve(timeRange);

    return timeRange;
  }

  /**
   * @ngdoc method
   * @name preserve
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Preserve parameters
   *
   * @param {Object} params parameters to set
   * @param {string} params.from from date
   * @param {string} params.to to date
   * @param {string} params.range name of the range
   */
  function preserve(params) {

    _params = angular.copy(params);

    // XXX: please don't set params by using `$location.search(params)` here, too many side effects due to the `$location.search` watch
    // HACK: $timeout is used because it appears that replace() replaces the entire route record when in current digest cycle
    $timeout(function() {
      $location.search('from', params.from).replace();
      $location.search('to', params.to).replace();
      $location.search('range', params.range).replace();
    });

  }

  /**
   * @ngdoc method
   * @name getStartDate
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Get current start date
   *
   * @returns {string} start date of the app
   */
  function getStartDate() {

    return _startDate;

  }

  /**
   * @ngdoc method
   * @name getToday
   * @methodOf adjust.common.service:TimeSpan
   *
   * @description
   * Get today's date formatted
   *
   * @returns {string} today's date
   */
  function getToday() {

    return _getToday().format(_dateFormat);

  }
}
export { TimeSpan };
